import {
  doc,
  getDoc,
  updateDoc,
  addDoc,
  setDoc,
  collection,
  deleteDoc,
  serverTimestamp,
  arrayUnion,
} from "firebase/firestore";
import {
  createUserWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
} from "firebase/auth";
// import moment from "moment"; // Removed to reduce bundle size
import { auth, db } from "../firebase";
import { familyTreeData } from "../pages/Account/family";

export const dateFormat = "MMMM D";

export async function createUserDocument(user: any, additionalData?: any) {
  if (!user) return;

  const userRef = doc(db, "users", user.uid);
  const userSnapshot = await getDoc(userRef);

  // Only create document if it doesn't exist
  if (!userSnapshot.exists()) {
    const { displayName, email } = user;
    const createdAt = new Date().toISOString();

    try {
      await setDoc(userRef, {
        displayName,
        email,
        createdAt,
        // Initialize with empty data structures that the app expects
        familyTreeData: familyTreeData,
        missionaryData: {},
        generationalData: {},
        // User profile fields
        name: displayName || "",
        phoneNumber: "",
        address: "",
        // Additional data from sign-up form or Google
        ...additionalData,
      });
    } catch (error) {
      console.error("Error creating user document:", error);
      throw error;
    }
  }
}

export function registerNewUser(
  data: any,
  successCallback: any,
  failureCallback: any
) {
  let error = false;
  let user: any;
  createUserWithEmailAndPassword(auth, data.email, data.password)
    .then(function (userCredential) {
      user = userCredential.user;
      // Update profile with display name
      return updateProfile(user, {
        displayName: data.firstName + " " + data.lastName,
      });
    })
    .then(() => {
      // Create Firestore user document
      return createUserDocument(user, {
        firstName: data.firstName,
        lastName: data.lastName,
        name: data.firstName + " " + data.lastName,
      });
    })
    .then(() => {
      // Send email verification
      return sendEmailVerification(user);
    })
    .then(() => {
      successCallback();
    })
    .catch(function (error) {
      console.error("Registration error:", error);
      switch (error.code) {
        case "auth/email-already-in-use":
          failureCallback("Email already in use");
          break;
        default:
          failureCallback("Problem with registration, please try again");
      }
    });
}

export function registerNewUserSendResetEmail(data: any) {
  createUserWithEmailAndPassword(auth, data.email, random())
    .then(function () {
      var user = auth.currentUser;

      sendPasswordResetEmail(auth, data.email)
        .then(() => {
          //  console.log('sent reset email');
        })
        .catch((error) => {
          const errorCode = error.code;
          const errorMessage = error.message;
        });
    })
    .catch(function (error) {});
}

//Utils

export function capitalizeFirstLetter(input: string) {
  return input && input.length > 0
    ? input.charAt(0).toUpperCase() + input.slice(1)
    : input;
}

const random = (length = 20) => {
  // Declare all characters used in UUID
  let chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

  // Pick characers randomly
  let str = "";
  for (let i = 0; i < length; i++) {
    str += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return str;
};

export const isValidEmail = (email: string) => {
  if (/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(email)) {
    return true;
  }
  return false;
};

// ============================================================================
// ORDER STATUS MANAGEMENT SYSTEM
// ============================================================================

// Define valid order statuses
export type OrderStatus =
  | "pending"
  | "awaiting-info"
  | "in-progress"
  | "ready-for-download"
  | "shipped"
  | "delivered"
  | "canceled";

// Define valid status transitions
const VALID_STATUS_TRANSITIONS: Record<OrderStatus, OrderStatus[]> = {
  pending: ["awaiting-info", "canceled"],
  "awaiting-info": ["in-progress", "canceled"],
  "in-progress": ["ready-for-download", "shipped", "canceled", "awaiting-info"], // Can go to ready-for-download when product is finished
  "ready-for-download": ["shipped", "canceled", "in-progress"], // Can ship product or go back to in-progress for more work
  shipped: ["delivered", "canceled"], // Can be delivered or canceled
  delivered: [], // Terminal state
  canceled: [], // Terminal state
};

// Status change reason types
export type StatusChangeReason =
  | "payment-confirmed"
  | "info-received"
  | "work-started"
  | "work-completed"
  | "finished-product-uploaded"
  | "customer-request"
  | "admin-action"
  | "system-auto"
  | "requires-more-info";

// Status change history entry
export interface StatusChangeEntry {
  fromStatus: OrderStatus | null;
  toStatus: OrderStatus;
  timestamp: any; // Firestore serverTimestamp
  reason: StatusChangeReason;
  notes?: string;
  userId?: string; // Who made the change (for admin actions)
  automated: boolean;
}

// Status update options
export interface StatusUpdateOptions {
  reason: StatusChangeReason;
  notes?: string;
  userId?: string; // For admin actions
  automated?: boolean;
  skipValidation?: boolean; // For admin overrides
}

/**
 * Validates if a status transition is allowed
 */
export function isValidStatusTransition(
  fromStatus: OrderStatus,
  toStatus: OrderStatus
): boolean {
  return VALID_STATUS_TRANSITIONS[fromStatus]?.includes(toStatus) || false;
}

/**
 * Gets the human-readable label for a status
 */
export function getStatusLabel(status: OrderStatus): string {
  switch (status) {
    case "pending":
      return "Submitted – Awaiting Review";
    case "awaiting-info":
      return "Awaiting Your Info";
    case "in-progress":
      return "In Progress";
    case "ready-for-download":
      return "Ready for Download";
    case "shipped":
      return "Shipped";
    case "delivered":
      return "Delivered";
    case "canceled":
      return "Canceled";
    default:
      return "Unknown";
  }
}

/**
 * Gets the color theme for a status
 */
export function getStatusColor(
  status: OrderStatus
): "warning" | "info" | "success" | "error" {
  switch (status) {
    case "pending":
      return "info";
    case "awaiting-info":
      return "warning";
    case "in-progress":
      return "info";
    case "ready-for-download":
      return "success";
    case "shipped":
      return "success";
    case "delivered":
      return "success";
    case "canceled":
      return "error";
    default:
      return "info";
  }
}

/**
 * Core function to update an order's status with validation and history tracking
 */
export async function updateOrderStatus(
  orderId: string,
  newStatus: OrderStatus,
  options: StatusUpdateOptions
): Promise<void> {
  try {
    // Get current order data
    const orderRef = doc(db, "orders", orderId);
    const orderSnap = await getDoc(orderRef);

    if (!orderSnap.exists()) {
      throw new Error(`Order ${orderId} not found`);
    }

    const orderData = orderSnap.data();
    const currentStatus = orderData.status as OrderStatus;

    // Skip if status is already the target status
    if (currentStatus === newStatus) {
      console.log(
        `Order ${orderId} already has status ${newStatus}, skipping update`
      );
      return;
    }

    // Validate transition unless explicitly skipped
    if (
      !options.skipValidation &&
      !isValidStatusTransition(currentStatus, newStatus)
    ) {
      throw new Error(
        `Invalid status transition from ${currentStatus} to ${newStatus} for order ${orderId}`
      );
    }

    // Create status change entry
    const statusChangeEntry: StatusChangeEntry = {
      fromStatus: currentStatus,
      toStatus: newStatus,
      timestamp: serverTimestamp(),
      reason: options.reason,
      notes: options.notes,
      userId: options.userId,
      automated: options.automated || false,
    };

    // Prepare update data
    const updateData: any = {
      status: newStatus,
      statusHistory: arrayUnion(statusChangeEntry),
      lastStatusUpdate: serverTimestamp(),
    };

    // Add completion timestamp for shipped/delivered orders
    if (newStatus === "shipped" || newStatus === "delivered") {
      updateData.completedAt = serverTimestamp();
    }

    // Update the order document
    await updateDoc(orderRef, updateData);

    console.log(
      `Successfully updated order ${orderId} from ${currentStatus} to ${newStatus}`
    );
  } catch (error) {
    console.error(`Error updating order status:`, error);
    throw error;
  }
}

/**
 * Auto-transition order status based on form completion
 */
export async function checkAndUpdateOrderProgress(
  orderId: string,
  formStatus: Record<string, boolean>
): Promise<void> {
  try {
    // Get current order data
    const orderRef = doc(db, "orders", orderId);
    const orderSnap = await getDoc(orderRef);

    if (!orderSnap.exists()) {
      throw new Error(`Order ${orderId} not found`);
    }

    const orderData = orderSnap.data();
    const currentStatus = orderData.status as OrderStatus;

    // Only auto-transition from awaiting-info status
    if (currentStatus !== "awaiting-info") {
      return;
    }

    // Check if all required forms are completed
    const allFormsCompleted = Object.values(formStatus).every(
      (completed) => completed === true
    );
    const hasAnyCompletedForms = Object.values(formStatus).some(
      (completed) => completed === true
    );

    if (allFormsCompleted) {
      // All forms completed - transition to in-progress
      await updateOrderStatus(orderId, "in-progress", {
        reason: "info-received",
        notes: "All required forms have been completed by the customer",
        automated: true,
      });
    } else if (hasAnyCompletedForms) {
      // Some forms completed - update the order to track progress but keep status
      await updateDoc(orderRef, {
        formStatus: formStatus,
        lastFormUpdate: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error(`Error checking order progress:`, error);
    throw error;
  }
}

/**
 * Saves form data as a draft without triggering status updates
 */
export async function saveOrderDraft(
  orderId: string,
  formType: string,
  formData: any
): Promise<void> {
  try {
    const orderRef = doc(db, "orders", orderId);
    
    // Update only the form data specific to this form type
    await updateDoc(orderRef, {
      [`formData.${formType}`]: formData,
      lastFormUpdate: serverTimestamp(),
    });

    console.log(`Draft saved for order ${orderId}, form ${formType}`);
  } catch (error) {
    console.error(`Error saving draft:`, error);
    throw error;
  }
}

/**
 * Get order status history
 */
export async function getOrderStatusHistory(
  orderId: string
): Promise<StatusChangeEntry[]> {
  try {
    const orderRef = doc(db, "orders", orderId);
    const orderSnap = await getDoc(orderRef);

    if (!orderSnap.exists()) {
      throw new Error(`Order ${orderId} not found`);
    }

    const orderData = orderSnap.data();
    return orderData.statusHistory || [];
  } catch (error) {
    console.error(`Error getting order status history:`, error);
    throw error;
  }
}

/**
 * Get orders by status (useful for admin dashboard)
 */
export async function getOrdersByStatus(status: OrderStatus): Promise<any[]> {
  try {
    // This would need to be implemented with proper Firestore queries
    // For now, returning empty array as placeholder

    return [];
  } catch (error) {
    console.error(`Error getting orders by status:`, error);
    throw error;
  }
}

// Test function to verify user document structure (for development/testing)
export async function testUserDocumentStructure(userId: string) {
  try {
    const userRef = doc(db, "users", userId);
    const userSnapshot = await getDoc(userRef);

    if (userSnapshot.exists()) {
      const userData = userSnapshot.data();
      console.log("✅ User document exists");
      console.log(
        "✅ Has familyTreeData:",
        Array.isArray(userData.familyTreeData)
      );
      console.log(
        "✅ Has missionaryData:",
        typeof userData.missionaryData === "object"
      );
      console.log(
        "✅ Has generationalData:",
        typeof userData.generationalData === "object"
      );
      console.log("✅ Has profile fields:", {
        name: !!userData.name,
        email: !!userData.email,
        displayName: !!userData.displayName,
      });
      return { success: true, data: userData };
    } else {
      return { success: false, error: "Document not found" };
    }
  } catch (error) {
    console.error("❌ Error testing user document:", error);
    return { success: false, error };
  }
}
